import React from 'react';
import { useDialogKeyboard, DialogKeyboardConfig } from 'lib/hooks/useDialogKeyboard';
import { closeModal, showModal, showIndependentModal } from './index';
import './tipsV2.scss';

export interface TipsModalV2Props {
  /** 内容支持纯文本 / 多段文本 / 富文本节点（如 <AgreementText />） */
  content: string | string[] | React.ReactNode;
  confirmCallback?: () => void;
  confirmText?: React.ReactNode;
  confirmStyle?: React.CSSProperties;
  title?: string;
  showClose?: boolean;
  footer?: React.ReactNode;
  keyboard?: DialogKeyboardConfig;
  /** 关闭弹窗时的额外回调（不负责关闭弹窗本身，仅用于埋点等副作用） */
  onClose?: () => void;
  /** 由 showIndependentModal 注入，用于卸载独立容器；外部调用方无需传入 */
  closeModal?: () => void;
}

const TipsModalV2 = ({
  content,
  confirmCallback,
  confirmText = '确定',
  confirmStyle,
  title,
  showClose = false,
  footer,
  keyboard,
  onClose,
  closeModal: closeModalProp,
}: TipsModalV2Props) => {
  const dismiss = () => {
    if (closeModalProp) {
      closeModalProp();
    } else {
      closeModal();
    }
  };

  // 用户主动取消（点击 ×、ESC）：销毁弹窗 + 触发 onClose 回调（用于埋点等副作用）
  const close = () => {
    dismiss();
    onClose?.();
  };

  // 用户点击确认按钮 / Enter：销毁弹窗 + 触发 confirmCallback；不再触发 onClose，避免回调重复
  const confirm = () => {
    dismiss();
    confirmCallback?.();
  };

  useDialogKeyboard({
    onEnter: keyboard?.onEnter ?? confirm,
    onEscape: keyboard?.onEscape ?? close,
    allowEnterInInput: keyboard?.allowEnterInInput,
  });

  const renderContent = () => {
    if (Array.isArray(content)) {
      return (
        <div className="tips-modalV2__contents-container">
          {content.map((item, index) => (
            <div key={index} className="tips-modalV2__contents-container__content">
              {item}
            </div>
          ))}
        </div>
      );
    }
    return <div className="tips-modalV2__content">{content}</div>;
  };

  return (
    <div className="tips-modalV2">
      {showClose ? <div className="tips-modalV2__close" onClick={close}></div> : null}
      {title ? <div className="tips-modalV2__title">{title}</div> : null}
      {renderContent()}
      <div className="tips-modalV2__btn" style={confirmStyle} onClick={confirm}>
        {confirmText}
      </div>
      {footer ? (
        <div className="tips-modalV2__footer">
          <div className="tips-modalV2__footer-content">{footer}</div>
        </div>
      ) : null}
    </div>
  );
};

TipsModalV2.show = (params: TipsModalV2Props) => {
  showModal(<TipsModalV2 {...params} />);
};

/**
 * 独立弹窗：不受全局 closeModal() 影响，可叠加在其他 modal 之上。
 * 适用于"二次确认"等需要独立生命周期的场景。
 */
TipsModalV2.showIndependent = (params: TipsModalV2Props) => {
  showIndependentModal(<TipsModalV2 {...params} />);
};

export default TipsModalV2;
